<script>
import { reactive } from "vue";
import { Head, Link, router } from '@inertiajs/vue3';
import BreezeLayout from '@/Layouts/Authenticated.vue';
import BreezeButton from '@/Components/Button.vue';
import BreezeInput from '@/Components/Input.vue';
import BreezeLabel from '@/Components/Label.vue';
import CheckOrXMark from "@/Components/Icons/CheckOrXMark.vue";
import AuditTrail from "@/Components/AuditTrail.vue";

export default {
    components: {
        AuditTrail,
      CheckOrXMark,
        BreezeLayout,
        BreezeButton,
        BreezeInput,
        BreezeLabel,
        Head,
        Link
    },
    props: {
        user: Object
    },
    setup(props) {

        const form = reactive({
            email: props.user.email,
            name: props.user.name,
            account_holder_code: props.user.profile.account_holder_code,
            second_account_holder_code: props.user.profile.second_account_holder_code,
        });

        console.log(JSON.stringify(props.user));

        const update = () => {
            router.put(route('users.update', { user: props.user.id }), form);
        };

        const edit = () => {
            router.get(route('users.edit', {user: props.user.id}));
        };

        const resetMFA = () => {
            router.patch(route('users.resetMFA', {user: props.user.id}));
        };

        const destroy = () => {
            if (confirm("You are about to delete user: "+ props.user.name + ", are you sure? This cannot be undone.")) {
              router.delete(route('users.destroy', props.user.id));
            }
        };

        return {
            form,
            update,
            edit,
            resetMFA,
            destroy
        };

    },
}
</script>

<template>
    <Head title="Edit user"/>

    <BreezeLayout>
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight flex">
                <Link :href="route('users.index')">Users</Link>
            </h2>

        </template>

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-gray-100">
                    <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                        <div class="mt-10 sm:mt-0">
                            <div class="md:grid md:grid-cols-3 md:gap-6">
                                <div class="md:col-span-1 flex justify-between flex-col">
                                    <div class="px-4 sm:px-0">
                                        <h3 class="text-lg font-medium leading-6 text-gray-900">User</h3>
                                        <p class="mt-5 text-sm text-gray-600">
                                            <strong>Few important details:</strong>
                                        </p>
                                        <ul class="mt-1 text-sm text-gray-600 list-disc">
                                            <li class="ml-5 mb-4">The `Active` flag means whether the user is allowed to login or not on the portal.</li>
                                            <li class="ml-5 mb-4">The `Account Holder Active` flag means that `account_holder_code` is activated in the BGS and user can fetch data from the API.</li>
                                            <li class="ml-5 mb-4">Root admins cannot be created through this UI. This is a manual change.</li>
                                            <li class="ml-5 mb-4">Any of the admin permissions will allow access to the IDS UI as well.</li>
                                            <li class="ml-5 mb-4">Changes made to a user are immediately effective for the portal. There is no delay.</li>
                                        </ul>
                                    </div>
                                    <div class="pb-2">
                                        <Link :href="route('users.index')" class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-slate-400 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-600">← BACK</Link>
                                    </div>
                                </div>
                                <div class="mt-5 md:mt-0 md:col-span-2">
                                    <form @submit.prevent="update">
                                        <div class="shadow overflow-hidden sm:rounded-md">
                                            <div class="px-4 py-5 bg-white sm:p-6 relative">

                                                <breeze-button
                                                    class="text-red-600 absolute end-5 top-3 p-1 text-xl"
                                                    type="button"
                                                    @click="resetMFA"
                                                >
                                                    Reset MFA
                                                </breeze-button>

                                              <breeze-button
                                                  class="text-red-600 absolute end-5 top-16 p-1 text-xl"
                                                  type="button"
                                                  @click="edit"
                                              >
                                                Edit
                                              </breeze-button>

                                              <breeze-button
                                                  class="text-red-500 bg-red-800 absolute end-5 bottom-3 p-1 text-xl"
                                                  type="button"
                                                  @click="destroy"
                                              >
                                                Remove
                                              </breeze-button>


                                            <table class="table-auto w-3/4">
                                                <caption class="hidden">User</caption>
                                                <tr>
                                                    <th class="text-left py-2">Fields</th>
                                                    <th class="text-left py-2">Values</th>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Username</td>
                                                    <td class="py-2">{{ user.name }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Email</td>
                                                    <td class="py-2">{{ user.email }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Account Holder Code</td>
                                                    <td class="py-2">{{ user.profile.account_holder_code }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Second Account Holder Code</td>
                                                    <td class="py-2">{{ user.profile.second_account_holder_code }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Account Holder Active</td>
                                                    <td class="py-2">
                                                      <CheckOrXMark :condition="(user.profile.account_holder_active)" />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Brand</td>
                                                    <td class="py-2">{{ user.brand_slug }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Roles</td>
                                                    <td class="py-2">{{ user.user_roles.join(", ") }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Active</td>
                                                    <td class="py-2">
                                                      <CheckOrXMark :condition="(user.active)" />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Created at</td>
                                                    <td class="py-2">{{ formatDate(user.created_at) }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="py-2">Last login at</td>
                                                    <td class="py-2">{{ formatDate(user.last_login_at) ?? 'Never' }}</td>
                                                </tr>

                                            </table>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        <AuditTrail :audits="user.audits" />

                    </div>
                </div>
            </div>
        </div>
    </BreezeLayout>
</template>
